import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Euro, CheckCircle, Clock, AlertCircle, X, Check, Ban, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';

const PayoutsManagement = () => {
  const [payouts, setPayouts] = useState([]);
  const [processing, setProcessing] = useState({});
  const [success, setSuccess] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [showPayModal, setShowPayModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [paymentReference, setPaymentReference] = useState('');

  useEffect(() => {
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      const response = await api.get('/admin/payouts');
      setPayouts(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const approvePayout = async (payoutId) => {
    setProcessing({ ...processing, [payoutId]: 'approve' });
    try {
      await api.put(`/admin/payouts/${payoutId}/approve`);
      setSuccess('Demande approuvée !');
      setTimeout(() => setSuccess(''), 3000);
      fetchPayouts();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setProcessing({ ...processing, [payoutId]: false });
    }
  };

  const rejectPayout = async (payoutId) => {
    setProcessing({ ...processing, [payoutId]: 'reject' });
    try {
      await api.put(`/admin/payouts/${payoutId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      setSuccess('Demande rejetée - Solde restauré');
      setTimeout(() => setSuccess(''), 3000);
      setShowRejectModal(null);
      setRejectReason('');
      fetchPayouts();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setProcessing({ ...processing, [payoutId]: false });
    }
  };

  const markAsPaid = async (payoutId) => {
    setProcessing({ ...processing, [payoutId]: 'pay' });
    try {
      await api.put(`/admin/payouts/${payoutId}/mark-paid?payment_reference=${encodeURIComponent(paymentReference)}`);
      setSuccess('Paiement marqué comme effectué !');
      setTimeout(() => setSuccess(''), 3000);
      setShowPayModal(null);
      setPaymentReference('');
      fetchPayouts();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setProcessing({ ...processing, [payoutId]: false });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#FFB800]/10 text-[#FFB800]">En attente</span>;
      case 'approved':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00FFFF]/10 text-[#00FFFF]">Approuvé</span>;
      case 'paid':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00FF88]/10 text-[#00FF88]">Payé</span>;
      case 'rejected':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#FF3B5C]/10 text-[#FF3B5C]">Rejeté</span>;
      default:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#6B6B6E]/10 text-[#6B6B6E]">{status}</span>;
    }
  };

  const pendingPayouts = payouts.filter(p => p.status === 'pending');
  const approvedPayouts = payouts.filter(p => p.status === 'approved');
  const paidPayouts = payouts.filter(p => p.status === 'paid');
  const rejectedPayouts = payouts.filter(p => p.status === 'rejected');
  
  const totalPending = pendingPayouts.reduce((acc, p) => acc + p.amount, 0);
  const totalApproved = approvedPayouts.reduce((acc, p) => acc + p.amount, 0);

  return (
    <div className="p-8" data-testid="payouts-management">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Paiements Vendeurs</h1>
        <p className="text-[#B8B8BA]">Gestion complète des demandes de retrait</p>
      </div>

      {success && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 mb-6 flex items-center space-x-3"
        >
          <CheckCircle className="h-5 w-5 text-[#00FF88]" />
          <span className="text-[#00FF88]">{success}</span>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA]">En attente</span>
            <Clock className="h-5 w-5 text-[#FFB800]" />
          </div>
          <div className="text-3xl font-bold text-[#FFB800]">{pendingPayouts.length}</div>
          <div className="text-sm text-[#6B6B6E] mt-1">{totalPending.toFixed(2)}€</div>
        </div>
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA]">Approuvés</span>
            <Check className="h-5 w-5 text-[#00FFFF]" />
          </div>
          <div className="text-3xl font-bold text-[#00FFFF]">{approvedPayouts.length}</div>
          <div className="text-sm text-[#6B6B6E] mt-1">{totalApproved.toFixed(2)}€ à payer</div>
        </div>
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA]">Payés</span>
            <CheckCircle className="h-5 w-5 text-[#00FF88]" />
          </div>
          <div className="text-3xl font-bold text-[#00FF88]">{paidPayouts.length}</div>
        </div>
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA]">Rejetés</span>
            <Ban className="h-5 w-5 text-[#FF3B5C]" />
          </div>
          <div className="text-3xl font-bold text-[#FF3B5C]">{rejectedPayouts.length}</div>
        </div>
      </div>

      {/* Pending Payouts */}
      {pendingPayouts.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
            <AlertCircle className="h-6 w-6 text-[#FFB800]" />
            <span>Demandes en attente ({pendingPayouts.length})</span>
          </h2>
          <div className="bg-[#1A1A1D] rounded-lg metallic-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#222226]">
                <tr>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Vendeur</th>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Email</th>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Montant</th>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Demandé le</th>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingPayouts.map((payout, index) => (
                  <tr key={payout.id} className="border-t border-[#3A3A3D] hover:bg-[#222226]">
                    <td className="px-6 py-4 text-white font-medium">{payout.vendor_email?.split('@')[0]}</td>
                    <td className="px-6 py-4 text-[#B8B8BA]">{payout.vendor_email}</td>
                    <td className="px-6 py-4 text-[#00FFFF] font-bold text-lg">{payout.amount}€</td>
                    <td className="px-6 py-4 text-[#B8B8BA] text-sm">{formatDate(payout.requested_at)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => approvePayout(payout.id)}
                          disabled={processing[payout.id]}
                          className="bg-[#00FF88] hover:bg-[#00E577] text-black px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                          title="Approuver"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowRejectModal(payout)}
                          disabled={processing[payout.id]}
                          className="bg-[#FF3B5C] hover:bg-[#FF2244] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                          title="Rejeter"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Approved Payouts - Ready to Pay */}
      {approvedPayouts.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
            <CreditCard className="h-6 w-6 text-[#00FFFF]" />
            <span>À payer ({approvedPayouts.length})</span>
          </h2>
          <div className="bg-[#1A1A1D] rounded-lg metallic-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#222226]">
                <tr>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Vendeur</th>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Email PayPal</th>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Montant</th>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Approuvé le</th>
                  <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {approvedPayouts.map((payout) => (
                  <tr key={payout.id} className="border-t border-[#3A3A3D] hover:bg-[#222226]">
                    <td className="px-6 py-4 text-white font-medium">{payout.vendor_email?.split('@')[0]}</td>
                    <td className="px-6 py-4 text-[#00FFFF]">{payout.vendor_email}</td>
                    <td className="px-6 py-4 text-[#00FFFF] font-bold text-lg">{payout.amount}€</td>
                    <td className="px-6 py-4 text-[#B8B8BA] text-sm">{formatDate(payout.approved_at)}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setShowPayModal(payout)}
                        className="btn-primary py-2 px-6"
                      >
                        Marquer payé
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-4">Historique complet</h2>
        <div className="bg-[#1A1A1D] rounded-lg metallic-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#222226]">
              <tr>
                <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Vendeur</th>
                <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Montant</th>
                <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Demandé le</th>
                <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Traité le</th>
                <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Statut</th>
                <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Référence</th>
              </tr>
            </thead>
            <tbody>
              {[...paidPayouts, ...rejectedPayouts].sort((a, b) => new Date(b.requested_at) - new Date(a.requested_at)).map((payout) => (
                <tr key={payout.id} className="border-t border-[#3A3A3D]">
                  <td className="px-6 py-4 text-white">{payout.vendor_email}</td>
                  <td className="px-6 py-4 text-white font-bold">{payout.amount}€</td>
                  <td className="px-6 py-4 text-[#B8B8BA] text-sm">{formatDate(payout.requested_at)}</td>
                  <td className="px-6 py-4 text-[#B8B8BA] text-sm">{formatDate(payout.paid_at || payout.rejected_at)}</td>
                  <td className="px-6 py-4">{getStatusBadge(payout.status)}</td>
                  <td className="px-6 py-4 text-[#6B6B6E] text-sm font-mono">{payout.payment_reference || payout.rejection_reason || '-'}</td>
                </tr>
              ))}
              {paidPayouts.length === 0 && rejectedPayouts.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-[#6B6B6E]">Aucun historique</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowRejectModal(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full mx-4"
          >
            <h3 className="text-xl font-bold text-white mb-4">Rejeter la demande</h3>
            <p className="text-[#B8B8BA] mb-4">
              Rejeter la demande de <span className="text-white font-medium">{showRejectModal.vendor_email}</span> pour <span className="text-[#00FFFF] font-bold">{showRejectModal.amount}€</span> ?
            </p>
            <div className="mb-4">
              <label className="block text-[#E8E8E8] mb-2">Raison du rejet (optionnel)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF3B5C] focus:outline-none"
                rows={3}
                placeholder="Ex: Informations manquantes..."
              />
            </div>
            <p className="text-[#FFB800] text-sm mb-4">Le solde sera automatiquement restauré au vendeur.</p>
            <div className="flex space-x-4">
              <button onClick={() => setShowRejectModal(null)} className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D]">
                Annuler
              </button>
              <button 
                onClick={() => rejectPayout(showRejectModal.id)} 
                disabled={processing[showRejectModal.id]}
                className="flex-1 bg-[#FF3B5C] text-white py-3 rounded-lg hover:bg-[#FF2244] disabled:opacity-50"
              >
                Rejeter
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Mark as Paid Modal */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowPayModal(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full mx-4"
          >
            <h3 className="text-xl font-bold text-white mb-4">Confirmer le paiement</h3>
            <p className="text-[#B8B8BA] mb-4">
              Confirmer le paiement de <span className="text-[#00FFFF] font-bold">{showPayModal.amount}€</span> à <span className="text-white font-medium">{showPayModal.vendor_email}</span> ?
            </p>
            <div className="mb-4">
              <label className="block text-[#E8E8E8] mb-2">Référence de paiement</label>
              <input
                type="text"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FF88] focus:outline-none"
                placeholder="Ex: PP-123456789"
              />
            </div>
            <div className="flex space-x-4">
              <button onClick={() => setShowPayModal(null)} className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D]">
                Annuler
              </button>
              <button 
                onClick={() => markAsPaid(showPayModal.id)} 
                disabled={processing[showPayModal.id]}
                className="flex-1 bg-[#00FF88] text-black py-3 rounded-lg hover:bg-[#00E577] font-medium disabled:opacity-50"
              >
                Confirmer
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PayoutsManagement;
